import {
  ChatUatAudience,
  ProjectValidationCase,
} from "../../lusca-search-api/generated-src";

export const DEFAULT_CHAT_UAT_AUDIENCE: ChatUatAudience = "customer";

export const CHAT_UAT_AUDIENCE_OPTIONS = [
  {
    label: "Customer",
    value: "customer",
    description: "Lead with financial meaning, sign-off impact, and owners.",
  },
  {
    label: "Engineer",
    value: "engineer",
    description:
      "Add evidence-backed code/configuration investigation and regression steps.",
  },
] as const;

const AUDIENCE_STORAGE_PREFIX = "lusca-chat-uat-audience";

export interface ChatUatPromptSuggestion {
  label: string;
  prompt: string;
}

interface ReplySuggestionInput {
  audience: ChatUatAudience;
  testCase?: ProjectValidationCase;
  hasArtefact: boolean;
  latestAssistant?: {
    isPassingReview?: boolean;
    isError?: boolean;
    action?: {
      type: string;
      outcome: string;
    };
  };
  askedPrompts?: string[];
}

const ENGINEER_REPLY_BY_ASSESSMENT: Record<
  ProjectValidationCase["assessment"],
  (subject: string) => ChatUatPromptSuggestion
> = {
  wrong_order: (subject) => ({
    label: "Trace scope divergence",
    prompt: `Trace the exact test-case or order evidence that makes ${subject} the wrong order, then recommend the next supported source to inspect.`,
  }),
  accounting_mismatch: (subject) => ({
    label: "Recommend code/config check",
    prompt: `Recommend the smallest evidence-backed code or configuration investigation for the accounting mismatches in ${subject}.`,
  }),
  cannot_verify: (subject) => ({
    label: "Identify missing telemetry",
    prompt: `Identify the missing telemetry or source evidence needed to diagnose ${subject} without guessing a cause.`,
  }),
  incomplete_scope: (subject) => ({
    label: "Refresh scope status",
    prompt: `Re-read the current Asana scope and Accounting/FBI approval for ${subject}. If it is approved, state Pass and treat the selected validation run as stale; otherwise list the remaining evidence gaps.`,
  }),
  matches_scope: (subject) => ({
    label: "Protect passing behaviour",
    prompt: `Recommend focused regression checks that would protect the passing behaviour observed in ${subject}.`,
  }),
};

const CUSTOMER_REPLY_BY_ASSESSMENT: Record<
  ProjectValidationCase["assessment"],
  (subject: string) => ChatUatPromptSuggestion
> = {
  wrong_order: (subject) => ({
    label: "Explain scope mismatch",
    prompt: `Explain in accounting terms why ${subject} does not match the intended test scope and what must be replaced.`,
  }),
  accounting_mismatch: (subject) => ({
    label: "Explain sign-off impact",
    prompt: `Explain the financial significance of each mismatch in ${subject} and what Accounting/FBI must confirm before sign-off.`,
  }),
  cannot_verify: (subject) => ({
    label: "List missing evidence",
    prompt: `List the evidence missing from ${subject}, why it blocks sign-off, and who should provide it.`,
  }),
  incomplete_scope: (subject) => ({
    label: "Check approval status",
    prompt: `Check the current Accounting/FBI approval and scope in Asana for ${subject}. If it is approved, state Pass and explain that validation only needs to be re-run to refresh the result; otherwise list the missing scope fields.`,
  }),
  matches_scope: (subject) => ({
    label: "Summarize sign-off evidence",
    prompt: `Summarize the evidence that supports sign-off for ${subject} and call out any remaining human confirmation.`,
  }),
};

function isChatUatAudience(value: unknown): value is ChatUatAudience {
  return value === "customer" || value === "engineer";
}

function audienceStorageKey(actorId?: string): string | undefined {
  const normalized = actorId?.trim();
  return normalized
    ? `${AUDIENCE_STORAGE_PREFIX}:${encodeURIComponent(normalized)}`
    : undefined;
}

export function readChatUatAudience(actorId?: string): ChatUatAudience {
  const key = audienceStorageKey(actorId);
  if (!key || typeof window === "undefined") {
    return DEFAULT_CHAT_UAT_AUDIENCE;
  }
  try {
    const stored = window.localStorage.getItem(key);
    return isChatUatAudience(stored) ? stored : DEFAULT_CHAT_UAT_AUDIENCE;
  } catch (_error) {
    return DEFAULT_CHAT_UAT_AUDIENCE;
  }
}

export function storeChatUatAudience(
  actorId: string | undefined,
  audience: ChatUatAudience
): void {
  const key = audienceStorageKey(actorId);
  if (!key || typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(key, audience);
  } catch (_error) {
    // Personalisation is optional when browser storage is unavailable.
  }
}

function promptSubject(testCase?: ProjectValidationCase): string {
  if (!testCase) {
    return "this UAT review";
  }
  const order = testCase.orderId ? ` for order ${testCase.orderId}` : "";
  return `the selected test case "${testCase.taskName}"${order}`;
}

export function buildChatUatStarterPrompts(
  audience: ChatUatAudience,
  testCase?: ProjectValidationCase
): ChatUatPromptSuggestion[] {
  if (!testCase) {
    if (audience === "engineer") {
      return [
        {
          label: "Diagnose likely boundary",
          prompt:
            "Help me diagnose a shadow-test failure. Ask for the order ID if it is not already available, inspect the evidence first, and only then recommend a supported code or configuration boundary.",
        },
        {
          label: "Recommend investigation",
          prompt:
            "Help me investigate a UAT accounting difference. Ask for the order or test-case reference you need, then separate proven evidence from engineering hypotheses.",
        },
        {
          label: "Plan regression coverage",
          prompt:
            "Help me design focused regression coverage for a shadow-test issue. Ask for the observed failure first and tie every proposed check to that evidence.",
        },
      ];
    }
    return [
      {
        label: "Review for sign-off",
        prompt:
          "Help me review a shadow-test order for UAT sign-off. Ask for the order ID if it is not already available, then lead with financial impact and owner actions.",
      },
      {
        label: "Explain financial differences",
        prompt:
          "Help me understand a UAT accounting difference. Ask for the order or test-case reference you need, then explain the evidence in accounting terms.",
      },
      {
        label: "Identify approval blockers",
        prompt:
          "Help me prepare a UAT case for sign-off. Ask for the relevant order or Asana test case, then list the evidence and human confirmations still required.",
      },
    ];
  }
  const subject = promptSubject(testCase);
  if (audience === "engineer") {
    return [
      {
        label: "Diagnose likely boundary",
        prompt: `Diagnose ${subject} from the shadow-test evidence. Identify the smallest supported code or configuration boundary to inspect, without guessing a package or path.`,
      },
      {
        label: "Recommend investigation",
        prompt: `Recommend evidence-backed investigation steps for ${subject}, using curated knowledge only where it directly applies.`,
      },
      {
        label: "Plan regression coverage",
        prompt: `Propose focused regression tests for ${subject} after the issue is corrected, tied to the observed difference.`,
      },
    ];
  }
  return [
    {
      label: "Review for sign-off",
      prompt: `Review ${subject} for UAT sign-off. Lead with the financial impact, distinguish observed evidence from interpretation, and state any owner action.`,
    },
    {
      label: "Explain financial differences",
      prompt: `Explain the booking, JEM, gamma, and prod differences for ${subject} in accounting terms, including which differences affect sign-off.`,
    },
    {
      label: "Identify approval blockers",
      prompt: `List the evidence Accounting/FBI must confirm before signing off ${subject}, and identify any unresolved owner.`,
    },
  ];
}

function actionSuggestions({
  audience,
  testCase,
  latestAssistant,
}: ReplySuggestionInput): ChatUatPromptSuggestion[] | undefined {
  const action = latestAssistant?.action;
  if (!action) {
    return undefined;
  }
  const subject = promptSubject(testCase);
  if (action.type === "run_shadow_test") {
    if (action.outcome === "confirmed") {
      return [
        {
          label: "Prepare re-test review",
          prompt: `When the new shadow-test result for ${subject} is available, compare it with the previous evidence and call out any changed verdicts.`,
        },
        audience === "engineer"
          ? {
              label: "Define regression checks",
              prompt: `Define the focused engineering checks that should pass before ${subject} is considered fixed, using only the evidence already available.`,
            }
          : {
              label: "Define sign-off checks",
              prompt: `List the financial evidence Accounting/FBI should verify in the new result for ${subject} before sign-off.`,
            },
      ];
    }
    if (action.outcome === "uncertain") {
      return [
        {
          label: "Verify queue status",
          prompt: `The shadow-test queue outcome for ${subject} was uncertain. Explain how to check for an accepted request or new result before any retry.`,
        },
        audience === "engineer"
          ? {
              label: "Inspect submission evidence",
              prompt: `Separate the confirmed submission evidence from the unknown queue outcome for ${subject}, then identify the safest engineering check before retrying.`,
            }
          : {
              label: "Avoid a duplicate run",
              prompt: `Explain what Accounting/FBI should check for ${subject} before requesting another shadow test, so a possibly accepted run is not duplicated.`,
            },
      ];
    }
    return [
      {
        label: "Resolve queue blocker",
        prompt: `Explain the confirmed reason the shadow test for ${subject} was not queued and the next evidence or owner needed before trying again.`,
      },
    ];
  }
  if (action.type === "add_asana_comment") {
    if (action.outcome === "uncertain") {
      return [
        {
          label: "Check Asana before retrying",
          prompt: `The comment outcome for ${subject} was uncertain. Explain what to check on the linked Asana task before attempting another comment.`,
        },
      ];
    }
    if (action.outcome === "failed") {
      return [
        {
          label: "Prepare manual handoff",
          prompt: `The Asana comment for ${subject} was not added. Summarize the review and owner action needed for a manual handoff without claiming the task was updated.`,
        },
      ];
    }
    return [
      {
        label: "Check remaining work",
        prompt: `Summarize what still needs to happen before ${subject} can be signed off.`,
      },
    ];
  }
  return undefined;
}

export function buildChatUatReplySuggestions(
  input: ReplySuggestionInput
): ChatUatPromptSuggestion[] {
  if (!input.latestAssistant || input.latestAssistant.isError) {
    return [];
  }
  const fromAction = actionSuggestions(input);
  const subject = promptSubject(input.testCase);
  let suggestions: ChatUatPromptSuggestion[];

  if (fromAction) {
    suggestions = fromAction;
  } else if (input.audience === "engineer") {
    const selectedRunMayBeStale =
      input.testCase?.assessment === "incomplete_scope";
    const primary = input.testCase
      ? ENGINEER_REPLY_BY_ASSESSMENT[input.testCase.assessment](subject)
      : {
          label: "Recommend next investigation",
          prompt: `Recommend the next evidence-backed code or configuration investigation for ${subject}, without inventing an implementation path.`,
        };
    suggestions = [
      primary,
      selectedRunMayBeStale
        ? {
            label: "Refresh validation result",
            prompt: `If current Accounting/FBI approval supersedes the selected run for ${subject}, explain how to refresh the validation result without inventing an engineering defect.`,
          }
        : {
            label: "Plan regression tests",
            prompt: `Propose focused regression tests for ${subject} that map directly to the observed difference and expected evidence.`,
          },
      input.testCase?.retestRecommended
        ? {
            label: "Define re-test gate",
            prompt: `State what should be fixed or verified before re-running the shadow test for ${subject}.`,
          }
        : {
            label: "Separate facts from hypotheses",
            prompt: `Separate the proven evidence, supported diagnosis, and remaining engineering hypotheses for ${subject}.`,
          },
    ];
  } else {
    const selectedRunMayBeStale =
      input.testCase?.assessment === "incomplete_scope";
    const primary = input.testCase
      ? CUSTOMER_REPLY_BY_ASSESSMENT[input.testCase.assessment](subject)
      : {
          label: "Explain financial impact",
          prompt: `Explain the financial and UAT sign-off impact of the available evidence for ${subject}.`,
        };
    suggestions = [
      primary,
      selectedRunMayBeStale
        ? {
            label: "Refresh validation result",
            prompt: `If current Accounting/FBI approval supersedes the selected run for ${subject}, explain how to refresh the green Pass status without asking for unnecessary scope changes.`,
          }
        : {
            label: "Identify owner and evidence",
            prompt: `Identify the next human owner for ${subject} and the exact evidence they need to confirm.`,
          },
      input.testCase?.retestRecommended
        ? {
            label: "Prepare for re-test",
            prompt: `State what Accounting/FBI should verify before re-running the shadow test for ${subject}.`,
          }
        : {
            label: "Draft sign-off summary",
            prompt: `Draft a concise sign-off summary for ${subject} that clearly separates facts, unresolved items, and owner actions.`,
          },
    ];
  }

  const asked = new Set(input.askedPrompts ?? []);
  return suggestions
    .filter(
      ({ prompt }, index, all) =>
        !asked.has(prompt) &&
        all.findIndex((candidate) => candidate.prompt === prompt) === index
    )
    .slice(0, input.hasArtefact ? 3 : 2);
}
